import Navigation from "../../common/components/Navigation/Navigation";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { shallowEqual } from "react-redux";
import type { RootState } from "../../common/state/store";
import type { INewProjectFormValues } from "../../features/projects/state/projectsSlice";
import {
  getProjectTagList,
  getProjectTagListSelector,
  setCurrentTemplateId,
  setNewProject,
} from "../../features/projects/state/projectsSlice";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import TemplateCategories from "../../features/projects/components/TemplateCategories";
import {
  getCurrentElements,
  getProjectGroupSize,
  getSelectedProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { FooterButtons } from "../../common/components/FooterButtons";
import { getCurrentUploadsStateList } from "src/features/gallery/state/gallerySlice";
import Chatbot from "../../features/chatbot/components/Chatbot";
import RagAgentMultiSelect from "../../features/rag/components/RagAgentMultiSelect";
import {
  getActiveGptConversation,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
  setLastAiModuleContext,
} from "../../features/chatbot/state/chatbotSlice";

export const GET_TEMPLATE_COMPONENT = "templateCompnent";

const SELECTED_BY_DEFAULT_TEMPLATE_ID = 390;

type CreationTab = "templates" | "ai";

const Templates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<CreationTab>("templates");
  const ragAgentIds = useAppSelector(getScenarioHelperRagAgentIds, shallowEqual);

  const { handleSubmit, control, setValue } = useForm<INewProjectFormValues>();

  useEffect(() => {
    void dispatch(getProjectTagList({ componentId: GET_TEMPLATE_COMPONENT }));
    dispatch(setCurrentTemplateId(SELECTED_BY_DEFAULT_TEMPLATE_ID));
    setValue("projectId", SELECTED_BY_DEFAULT_TEMPLATE_ID);
  }, []);

  const { tagList, currentProjectGroup, uploads, activeGptConversation } = useAppSelector(
    (state: RootState) => {
      return {
        tagList: getProjectTagListSelector(state),
        currentProjectGroup: getSelectedProjectGroup(state),
        uploads: getCurrentUploadsStateList(state),
        activeGptConversation: getActiveGptConversation(state),
      };
    },
  );

  const [formHeight, setFormHeight] = useState(0);

  const onSubmit: SubmitHandler<INewProjectFormValues> = (data) => {
    dispatch(setNewProject({ ...data, languageId: 1 }));
    navigate(`${data.projectId}`);
  };

  const { selectedProjectGroup, currentElements } = useAppSelector((state: RootState) => {
    return {
      selectedProjectGroup: getSelectedProjectGroup(state),
      currentElements: getCurrentElements(state),
    };
  });

  const [isComputingPgSize, setIsComputingPgSize] = useState(false);
  const [isOverSizeLimitForProjectGroup, setIsOverSizeLimitForProjectGroup] = useState(false);

  useEffect(() => {
    if (currentElements.length > 0) {
      setIsComputingPgSize(true);
      void dispatch(
        getProjectGroupSize({
          projectsIds: currentElements
            .flatMap((pge) => (pge.project ? [pge.project.id] : []))
            .join(","),
        }),
      )
        .then((res) => {
          if (getProjectGroupSize.fulfilled.match(res) && res.payload.data > 10737418240) {
            setIsOverSizeLimitForProjectGroup(true);
          }
        })
        .finally(() => {
          setIsComputingPgSize(false);
        });
    }
  }, []);

  const handleGenerateProjectFromAi = () => {
    if (!activeGptConversation?.id) return;
    dispatch(
      setLastAiModuleContext({
        conversationId: activeGptConversation.id,
        relatedGptOutputIndex: activeGptConversation.conversation_data.output.length - 1,
        generateBlockadeImages: false,
      }),
    );
    dispatch(
      setNewProject({
        projectId: 0,
        newName: "",
        languageId: 1,
        thumbnailId: 0,
        enabledLanguagesIds: [],
        projectGroupId: currentProjectGroup.id,
      }),
    );
    navigate("ai");
  };

  const getNextDisabledTooltipText = () => {
    if (isComputingPgSize) {
      return t("tooltip.computingPgSize");
    } else if (isOverSizeLimitForProjectGroup) {
      return t("tooltip.cannotCreateProjectProjectGroupTooLarge");
    } else {
      return "";
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const navbarElement = document.getElementById("navbar");
      const uploadBarElement = document.getElementById("upload-bar-overlay");
      const navbarHeight = uploadBarElement?.clientHeight || 0;
      const uploadBarHeight = navbarElement?.clientHeight || 0;
      const windowHeight = document.documentElement.clientHeight;

      setFormHeight(windowHeight - navbarHeight - uploadBarHeight);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [uploads]);

  const navigationTabs = [
    {
      title: t("chooseTemplateType.tabBrowseTemplates"),
      urlEndPoint: "",
      preventNavigation: true,
      isActive: activeTab === "templates",
      onClick: () => setActiveTab("templates"),
    },
    {
      title: t("chooseTemplateType.tabCreateWithAi"),
      urlEndPoint: "",
      preventNavigation: true,
      isActive: activeTab === "ai",
      onClick: () => setActiveTab("ai"),
    },
  ];

  return (
    <>
      <Navigation
        title={t("pages.createProject.title")}
        showGoBackButton
        backCustomRedirectTo={`/courses/${currentProjectGroup.id}/list`}
        tabs={navigationTabs}
      />
      <div className="w-full xl:px-0 ">
        {activeTab === "templates" ? (
          <form
            key={formHeight}
            onSubmit={handleSubmit(onSubmit)}
            style={{ height: `${formHeight}px` }}
            className="flex flex-col justify-between"
          >
            <h1 className="text-2xl font-medium pl-4 pr-4 pt-2">{t("chooseTemplateType.title")}</h1>
            <p className="mb-6 mt-4 pl-4 pr-4">{t("chooseTemplateType.whatsTemplate")}</p>

            <div className="inline-block w-full pl-4 pr-4 overflow-auto flex-grow  personalize-scroll-visible">
              <TemplateCategories setValue={setValue} control={control} tagList={tagList} />
            </div>

            <div className="bg-gray-100 w-full flex-shrink-0">
              <FooterButtons
                cancelText={t("general.cancel")}
                cancelCb={() => navigate(`/courses/${selectedProjectGroup.id}/list`)}
                backText={t("general.back")}
                backDisabled
                nextText={t("general.next")}
                nextDisabled={isComputingPgSize || isOverSizeLimitForProjectGroup}
                nextDisabledTooltipText={getNextDisabledTooltipText()}
              />
            </div>
          </form>
        ) : (
          <div
            className="grid overflow-hidden"
            style={{ height: `${formHeight}px`, gridTemplateColumns: "1fr 280px" }}
          >
            <div className="min-w-0 overflow-hidden">
              <Chatbot
                mode="scenarioHelper"
                activeConversation={null}
                onGenerateProject={handleGenerateProjectFromAi}
              />
            </div>
            <div className="bg-gray-50 border-l border-gray-200 p-4 overflow-y-auto overflow-x-hidden">
              <RagAgentMultiSelect
                value={ragAgentIds}
                onChange={(ids) => dispatch(setScenarioHelperRagAgentIds(ids))}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Templates;
